import { Link, useNavigate } from 'react-router';
import { CircleCheckBig, Clock, MessageSquareWarning, Radio, Sparkles, Upload } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useT } from '@/i18n';
import { ChannelChip, Empty, Ltr, QueryView, StatusPill } from '@/components/ui';
import { ActivityFeed, MyTasksCard } from '@/components/tasks';
import { useOverview, useProjects } from '@/lib/api/hooks';
import { useSession, useMembership } from '@/store/session';
import { can } from '@/lib/constants';
import { greetingKey, relativeDay, shortDate, timecode } from '@/lib/format';
import type { Overview } from '@/lib/api/types';

const ATTN: Record<string, { icon: LucideIcon; bg: string; fg: string; key: 'attnChanges' | 'attnReview' | 'attnClient' }> = {
  changes: { icon: MessageSquareWarning, bg: 'var(--red-soft)', fg: 'var(--accent-text)', key: 'attnChanges' },
  review: { icon: CircleCheckBig, bg: 'var(--amber-soft)', fg: 'var(--amber)', key: 'attnReview' },
  client: { icon: Clock, bg: 'var(--blue-soft)', fg: 'var(--blue-text)', key: 'attnClient' },
};

function ActionCard({ icon: Icon, colour, title, sub, children }: { icon: LucideIcon; colour: string; title: string; sub: string; children: React.ReactNode }) {
  return (
    <div className="card card-pad">
      <div className="row" style={{ gap: 10 }}>
        <Icon size={18} aria-hidden style={{ color: colour }} />
        <h2 className="display" style={{ fontWeight: 600, fontSize: 16, margin: 0 }}>{title}</h2>
      </div>
      <p className="muted" style={{ fontSize: 13, lineHeight: '20px', margin: 0 }}>{sub}</p>
      {children}
    </div>
  );
}

function HomeContent({ data }: { data: Overview }) {
  const { t, locale } = useT();
  const navigate = useNavigate();
  const projects = useProjects();
  const colourOf = (projectId: string) => projects.data?.find((p) => p.id === projectId)?.colours?.primary ?? 'var(--muted)';
  return (
    <>
      <div className="grid-2">
        <section className="card" aria-labelledby="attn-h">
          <div className="card-head">
            <h2 id="attn-h" className="h2">{t('attention')}</h2>
            {data.attention.length ? <span className="badge" style={{ background: 'var(--accent)', color: 'var(--on-accent)' }}>{data.attention.length}</span> : null}
          </div>
          {data.attention.length === 0 ? <Empty>{t('noAttention')}</Empty> : null}
          {data.attention.map((a) => {
            const m = ATTN[a.status] ?? ATTN.review;
            const Icon = m.icon;
            return (
              <button key={a.id} type="button" className="clickable card-row" onClick={() => navigate(`/projects/${a.projectId}/episodes/${a.id}`)} style={{ display: 'grid', gridTemplateColumns: '30px minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
                <span style={{ width: 30, height: 30, borderRadius: 6, background: m.bg, display: 'grid', placeItems: 'center' }}>
                  <Icon size={15} aria-hidden style={{ color: m.fg }} />
                </span>
                <span className="stack" style={{ gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{a.number ? `${a.number} · ` : ''}{a.title}</span>
                  <span className="muted small">{t(m.key, { project: a.projectTitle })}</span>
                </span>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--accent-text)' }}>{t('open')}</span>
              </button>
            );
          })}
        </section>
        <section className="card" aria-labelledby="next-h">
          <div className="card-head">
            <h2 id="next-h" className="h2">{t('upNext')}</h2>
            <Link to="/calendar" className="push small">{t('openCal')}</Link>
          </div>
          {data.upNext.length === 0 ? <Empty>{t('noUpNext')}</Empty> : null}
          {data.upNext.map((u) => (
            <div key={u.id} className="card-row" style={{ display: 'grid', gridTemplateColumns: '120px minmax(0,1fr) auto', gap: 12, alignItems: 'center', padding: '10px 18px' }}>
              <span className="muted small">{relativeDay(locale, u.scheduledAt, { today: t('today'), tomorrow: t('tomorrow') })}</span>
              <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                <span className="ellipsis" style={{ fontSize: 13, fontWeight: 500 }}>{u.title}</span>
                <span className="muted small">{u.projectTitle}</span>
              </span>
              <span className="row" style={{ gap: 6 }}>
                {/* A post of a video waits for its approval chain (plan §17.3 item 2); scheduled posts need no pill. */}
                {u.status !== 'scheduled' ? <StatusPill status={u.status} label={u.status === 'review' ? t('postInternalReview') : undefined} title={u.status === 'review' || u.status === 'client' ? t('postWaitsTip') : undefined} /> : null}
                <ChannelChip channel={u.platform} />
              </span>
            </div>
          ))}
        </section>
      </div>

      <section className="card" aria-labelledby="recent-h">
        <div className="card-head"><h2 id="recent-h" className="h2">{t('recent')}</h2></div>
        {data.recent.length === 0 ? <Empty>{t('noRecent')}</Empty> : null}
        {data.recent.map((e) => (
          <button key={e.id} type="button" className="clickable card-row" onClick={() => navigate(`/projects/${e.projectId}/episodes/${e.id}`)} style={{ display: 'grid', gridTemplateColumns: 'minmax(90px,150px) 44px minmax(0,1fr) 80px 80px 130px', gap: 14, alignItems: 'center', fontSize: 14, padding: '11px 18px' }}>
            <span className="row muted small" style={{ minWidth: 0 }}>
              <span className="swatch" style={{ background: colourOf(e.projectId) }} />
              <span className="ellipsis">{e.projectTitle}</span>
            </span>
            <span className="mono small muted">{e.number}</span>
            <span className="ellipsis" style={{ fontWeight: 500 }}>{e.title}</span>
            <span className="muted hide-narrow" style={{ fontSize: 13 }}>{shortDate(locale, e.recordedAt)}</span>
            <Ltr className="mono small muted hide-narrow">{e.durationMs ? timecode(e.durationMs) : '—'}</Ltr>
            <span style={{ justifySelf: 'start' }}><StatusPill status={e.status} /></span>
          </button>
        ))}
      </section>
    </>
  );
}

export function HomePage() {
  const { t } = useT();
  const user = useSession((s) => s.user);
  const membership = useMembership();
  const overview = useOverview();
  const firstName = user?.name.split(/\s+/)[0] ?? '';
  const s = overview.data?.stats;
  return (
    <div className="page" style={{ maxWidth: 1320, gap: 24 }}>
      <div className="page-head">
        <div className="stack">
          <h1 className="h1 h1-lg">{t(greetingKey(), { name: firstName })}</h1>
          {s ? <span className="muted">{t('homeSub', { customers: s.customers, projects: s.projects, posts: s.postsThisWeek })}</span> : null}
        </div>
        <div className="row">
          <Link to="/calendar" className="btn">{t('openCal')}</Link>
          {can(membership?.role, 'projects.manage') ? <Link to="/projects?new=1" className="btn btn-primary">{t('newProject')}</Link> : null}
        </div>
      </div>

      <div className="grid-cards">
        <ActionCard icon={Upload} colour="var(--text)" title={t('uploadCardTitle')} sub={t('uploadCardSub')}>
          <Link to="/projects" className="btn btn-primary" style={{ justifySelf: 'start', height: 36 }}>{t('uploadCardBtn')}</Link>
        </ActionCard>
        <ActionCard icon={Radio} colour="var(--accent)" title={t('nLive')} sub={t('liveSub')}>
          <Link to="/live" className="btn btn-quiet" style={{ justifySelf: 'start', height: 36 }}>{t('open')}</Link>
        </ActionCard>
        <ActionCard icon={Sparkles} colour="var(--blue-text)" title={t('nDirector')} sub={t('dirSubShort')}>
          <Link to="/soon/director" className="btn btn-quiet" style={{ justifySelf: 'start', height: 36 }}>{t('comingSoonPhase', { phase: 'P2' })}</Link>
        </ActionCard>
      </div>

      <QueryView query={overview}>{(data) => <HomeContent data={data} />}</QueryView>

      {/* Tasks and the activity feed (plan §19.4); hidden for client reviewers, who have no tasks. */}
      {can(membership?.role, 'tasks.view') ? (
        <div className="grid-2">
          <MyTasksCard />
          <ActivityFeed limit={8} />
        </div>
      ) : null}
    </div>
  );
}
